import React, { useMemo } from 'react';
import * as css from './praksisstedsok.module.scss';
import { useIntl } from 'react-intl';
import translations from './praksisstedsok.i18n';
import classNames from 'classnames';
import {
  MinimalPraksisavtaleDto,
  MinimalPraksisbestillingDto,
  MinimalPraksisbesvarelseDto,
  MinimalPraksisstedtilgangDto,
  MinimalPraksisstedtilgangDtoTilgangstypeDtoEnum,
  MinimalStudentPraksisplassDto,
  PraksisavtaleStedBestillingspunktTypeDto,
} from '../../api/generated/admin-generated-api';
import { InformationDialog } from '../../components/dialog/informationDialog';
import PanelHeader from '../../components/panelheader/panelHeader';
import { isNullOrUndefined } from '../../util/isNullOrUndefined';
import {
  AvtaleProps,
  AvtalerProps,
  BestillingerProps,
  BestillingProps,
  BesvarelseProps,
  BesvarelserProps,
  BoldRegularPairProps,
  PraksisstedInfoModalProps,
  PraksisstedTilgangerProps,
  PraksisstedTilgangProps,
  StudentPraksisplasserProps,
  StudentPraksisplassProps,
  SubSectionProps,
} from './types';
import {
  getAccArraysForUndernoder,
  hasAnyRelevantChildFlattenedUndernoder,
  useShowOnSelfOrChildren,
} from './functions';

const Entry: React.FC<React.PropsWithChildren> = ({ children }) => (
  <p className={css.propertyEntry}>{children}</p>
);

const BoldRegularPair: React.FC<
  React.PropsWithChildren<BoldRegularPairProps>
> = ({ boldString, regularString, large }) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      none: formatMessage({ id: translations.none.id }),
    }),
    [formatMessage],
  );

  return (
    <span className={classNames(css.boldRegularPair, large && css.large)}>
      <b className={css.bold}>{boldString}</b>
      <span className={css.regular}>{regularString || labels.none}</span>
    </span>
  );
};

const PraksisstedInfoModal: React.FC<
  React.PropsWithChildren<PraksisstedInfoModalProps>
> = ({
  praksissted,
  onClose,
  flattenedUndernoder,
  flattenedTree,
  showOvernodeWarning,
}) => {
  const {
    id,
    navn,
    kode,
    statusAktiv,
    nivaa,
    relevant,
    praksisavtaler,
    praksisbestillinger,
    praksisbesvarelser,
    studentPraksisplasser,
    praksisstedtilganger,
  } = praksissted;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      close: formatMessage({ id: translations.close.id }),
      yes: formatMessage({ id: translations.yes.id }),
      no: formatMessage({ id: translations.no.id }),
      modalTitle: formatMessage({ id: translations.modalTitle.id }, { navn }),
      modalNavn: formatMessage({ id: translations.modalNavn.id }),
      modalKode: formatMessage({ id: translations.modalKode.id }),
      modalPraksisstedId: formatMessage({
        id: translations.modalPraksisstedId.id,
      }),
      modalAktiv: formatMessage({ id: translations.modalAktiv.id }),
      modalNivaa: formatMessage({ id: translations.modalNivaa.id }),
      modalRelevant: formatMessage({ id: translations.modalRelevant.id }),
      modalRelevanteUndernoder: formatMessage({
        id: translations.modalRelevanteUndernoder.id,
      }),
      modalRelevantWarning: formatMessage({
        id: translations.modalRelevantWarning.id,
      }),
      modalOvernodeWarning: formatMessage({
        id: translations.modalOvernodeWarning.id,
      }),
    }),
    [formatMessage, navn],
  );

  const isRelevantBecauseOfChildren = useMemo(
    () => hasAnyRelevantChildFlattenedUndernoder(flattenedUndernoder),
    [flattenedUndernoder],
  );

  const {
    praksisavtaler: avtalerOnChildren,
    praksisbesvarelser: besvarelserOnChildren,
    praksisstedtilganger: praksisstedtilgangerOnChildren,
    studentPraksisplasser: studentPraksisplasserOnChildren,
  } = useMemo(
    () => getAccArraysForUndernoder(flattenedUndernoder),
    [flattenedUndernoder],
  );

  const bestillingerInHierarchy = useMemo(
    () =>
      flattenedTree.reduce(
        (acc: MinimalPraksisbestillingDto[], node) =>
          acc.concat(node.praksisbestillinger),
        [],
      ),
    [flattenedTree],
  );

  const bestillingerOnAvtaleIdsButNotOnPraksissted = useMemo(
    () =>
      bestillingerInHierarchy.filter(
        (bestilling) =>
          [...praksisavtaler, ...avtalerOnChildren]
            .map((avtale) => avtale.avtaleId)
            .includes(bestilling.avtaleId) &&
          !praksisbestillinger
            .map((praksisbestilling) => praksisbestilling.bestillingId)
            .includes(bestilling.bestillingId),
      ),
    [
      bestillingerInHierarchy,
      avtalerOnChildren,
      praksisavtaler,
      praksisbestillinger,
    ],
  );

  return (
    <InformationDialog
      title={labels.modalTitle}
      onClose={onClose}
      submitLabel={labels.close}
      extraClasses={[css.praksisstedInfoModal]}
      isOpen
    >
      <Entry>
        <BoldRegularPair
          boldString={labels.modalNavn}
          regularString={navn}
          large
        />
      </Entry>
      <Entry>
        <BoldRegularPair
          boldString={labels.modalKode}
          regularString={kode}
          large
        />
      </Entry>
      <Entry>
        <BoldRegularPair
          boldString={labels.modalPraksisstedId}
          regularString={id}
          large
        />
      </Entry>
      <Entry>
        <BoldRegularPair
          boldString={labels.modalAktiv}
          regularString={statusAktiv ? labels.yes : labels.no}
          large
        />
      </Entry>
      <Entry>
        <BoldRegularPair
          boldString={labels.modalNivaa}
          regularString={`${nivaa + 1}`}
          large
        />
      </Entry>
      <Entry>
        <BoldRegularPair
          boldString={labels.modalRelevant}
          regularString={relevant ? labels.yes : labels.no}
          large
        />
      </Entry>
      <Entry>
        <BoldRegularPair
          boldString={labels.modalRelevanteUndernoder}
          regularString={isRelevantBecauseOfChildren ? labels.yes : labels.no}
          large
        />
      </Entry>
      {(relevant || isRelevantBecauseOfChildren) && (
        <Entry>
          <b className={css.bold}>{labels.modalRelevantWarning}</b>
        </Entry>
      )}
      {showOvernodeWarning && (
        <Entry>
          <b className={css.bold}>{labels.modalOvernodeWarning}</b>
        </Entry>
      )}
      <Avtaler
        avtalerOnSelf={praksisavtaler}
        avtalerOnChildren={avtalerOnChildren}
      />
      <Bestillinger
        bestillingerOnSelf={praksisbestillinger}
        relevanteBestillinger={bestillingerOnAvtaleIdsButNotOnPraksissted}
      />
      <Besvarelser
        besvarelserOnSelf={praksisbesvarelser}
        besvarelserOnChildren={besvarelserOnChildren}
      />
      <StudentPraksisplasser
        studentPraksisplasserOnSelf={studentPraksisplasser}
        studentPraksisplasserOnChildren={studentPraksisplasserOnChildren}
      />
      <PraksisstedTilganger
        praksisstedtilgangerOnSelf={praksisstedtilganger}
        praksisstedtilgangerOnChildren={praksisstedtilgangerOnChildren}
      />
    </InformationDialog>
  );
};

const SubSection: React.FC<React.PropsWithChildren<SubSectionProps>> = ({
  header,
  children,
}) => (
  <div className={css.subSection}>
    <h3 className={css.header}>{header}</h3>
    <div className={css.subSectionContent}>{children}</div>
  </div>
);

const Avtale: React.FC<React.PropsWithChildren<AvtaleProps>> = ({ avtale }) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalAvtaleNavn: formatMessage({ id: translations.modalAvtaleNavn.id }),
      modalAvtaleId: formatMessage({ id: translations.modalAvtaleId.id }),
      modalAvtaleStart: formatMessage({ id: translations.modalAvtaleStart.id }),
      modalAvtaleSlutt: formatMessage({ id: translations.modalAvtaleSlutt.id }),
      modalAvtaleAktiv: formatMessage({ id: translations.modalAvtaleAktiv.id }),
      yes: formatMessage({ id: translations.yes.id }),
      no: formatMessage({ id: translations.no.id }),
    }),
    [formatMessage],
  );

  return (
    <Entry>
      <BoldRegularPair
        boldString={labels.modalAvtaleNavn}
        regularString={avtale.praksisavtaleNavn}
      />
      <BoldRegularPair
        boldString={labels.modalAvtaleId}
        regularString={avtale.avtaleId}
      />
      <BoldRegularPair
        boldString={labels.modalAvtaleStart}
        regularString={avtale.avtalestart}
      />
      <BoldRegularPair
        boldString={labels.modalAvtaleSlutt}
        regularString={avtale.avtaleslutt}
      />
      <BoldRegularPair
        boldString={labels.modalAvtaleAktiv}
        regularString={avtale.statusAktiv === 'J' ? labels.yes : labels.no}
      />
    </Entry>
  );
};

const mapToAvtaler = (avtaler: MinimalPraksisavtaleDto[]): JSX.Element[] =>
  avtaler.map((avtale) => (
    <Avtale key={`avtale-${avtale.avtaleId}`} avtale={avtale} />
  ));

const Avtaler: React.FC<React.PropsWithChildren<AvtalerProps>> = ({
  avtalerOnSelf,
  avtalerOnChildren,
}) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalAvtaler: formatMessage({ id: translations.modalAvtaler.id }),
      modalAvtalerSted: formatMessage({ id: translations.modalAvtalerSted.id }),
      modalAvtalerUndernoder: formatMessage({
        id: translations.modalAvtalerUndernoder.id,
      }),
      none: formatMessage({ id: translations.none.id }),
    }),
    [formatMessage],
  );

  const { onSelf: showAvtalerOnSelf, onChildren: showAvtalerOnChildren } =
    useShowOnSelfOrChildren(avtalerOnSelf, avtalerOnChildren);

  return (
    <PanelHeader title={labels.modalAvtaler} collapsible>
      {showAvtalerOnSelf && (
        <SubSection header={labels.modalAvtalerSted}>
          {mapToAvtaler(avtalerOnSelf)}
        </SubSection>
      )}
      {showAvtalerOnChildren && (
        <SubSection header={labels.modalAvtalerUndernoder}>
          {mapToAvtaler(avtalerOnChildren)}
        </SubSection>
      )}
      {!showAvtalerOnSelf && !showAvtalerOnChildren && labels.none}
    </PanelHeader>
  );
};

const Bestilling: React.FC<React.PropsWithChildren<BestillingProps>> = ({
  bestilling,
}) => {
  const {
    bestillingId,
    bestilling: bestillingAntall,
    bestillingsmodus,
    avtaleId,
    praksisstedId,
    delt,
    aktivitetId,
  } = bestilling;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalBestillingAvtaleSted: formatMessage({
        id: translations.modalBestillingAvtaleSted.id,
      }),
      modalBestillingOmfattetSted: formatMessage({
        id: translations.modalBestillingOmfattetSted.id,
      }),
      modalBestillingId: formatMessage({
        id: translations.modalBestillingId.id,
      }),
      modalAvtaleId: formatMessage({ id: translations.modalAvtaleId.id }),
      modalPraksisstedId: formatMessage({
        id: translations.modalPraksisstedId.id,
      }),
      modalAktivitetId: formatMessage({ id: translations.modalAktivitetId.id }),
      modalBestillingDelt: formatMessage({
        id: translations.modalBestillingDelt.id,
      }),
      modalBestillingModus: formatMessage({
        id: translations.modalBestillingModus.id,
      }),
      modalBestillingAntall: formatMessage({
        id: translations.modalBestillingAntall.id,
      }),
      yes: formatMessage({ id: translations.yes.id }),
      no: formatMessage({ id: translations.no.id }),
    }),
    [formatMessage],
  );

  const deltMsg = useMemo(
    () => (delt ? labels.yes : labels.no),
    [delt, labels],
  );

  const bestillingsmodusMsg = useMemo(
    () =>
      bestillingsmodus === PraksisavtaleStedBestillingspunktTypeDto.AvtaleSted
        ? labels.modalBestillingAvtaleSted
        : labels.modalBestillingOmfattetSted,
    [bestillingsmodus, labels],
  );

  return (
    <Entry>
      <BoldRegularPair
        boldString={labels.modalBestillingId}
        regularString={bestillingId}
      />
      <BoldRegularPair
        boldString={labels.modalAvtaleId}
        regularString={avtaleId}
      />
      <BoldRegularPair
        boldString={labels.modalPraksisstedId}
        regularString={praksisstedId}
      />
      <BoldRegularPair
        boldString={labels.modalAktivitetId}
        regularString={aktivitetId}
      />
      <BoldRegularPair
        boldString={labels.modalBestillingDelt}
        regularString={deltMsg}
      />
      <BoldRegularPair
        boldString={labels.modalBestillingModus}
        regularString={bestillingsmodusMsg}
      />
      <BoldRegularPair
        boldString={labels.modalBestillingAntall}
        regularString={`${bestillingAntall}`}
      />
    </Entry>
  );
};

const mapToBestillinger = (
  bestillinger: MinimalPraksisbestillingDto[],
): JSX.Element[] =>
  bestillinger.map((bestilling) => (
    <Bestilling
      key={`bestilling-${bestilling.bestillingId}`}
      bestilling={bestilling}
    />
  ));

const Bestillinger: React.FC<React.PropsWithChildren<BestillingerProps>> = ({
  bestillingerOnSelf,
  relevanteBestillinger,
}) => {
  const {
    onSelf: showBestillingerOnSelf,
    onChildren: showRelevanteBestillinger,
  } = useShowOnSelfOrChildren(bestillingerOnSelf, relevanteBestillinger);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalBestillinger: formatMessage({
        id: translations.modalBestillinger.id,
      }),
      modalBestillingerVedPraksissted: formatMessage({
        id: translations.modalBestillingerVedPraksissted.id,
      }),
      modalBestillingerRelevant: formatMessage({
        id: translations.modalBestillingerRelevant.id,
      }),
      none: formatMessage({
        id: translations.none.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <PanelHeader title={labels.modalBestillinger} collapsible>
      {showBestillingerOnSelf && (
        <SubSection header={labels.modalBestillingerVedPraksissted}>
          {mapToBestillinger(bestillingerOnSelf)}
        </SubSection>
      )}
      {showRelevanteBestillinger && (
        <SubSection header={labels.modalBestillingerRelevant}>
          {mapToBestillinger(relevanteBestillinger)}
        </SubSection>
      )}
      {!showBestillingerOnSelf && !showRelevanteBestillinger && labels.none}
    </PanelHeader>
  );
};

const Besvarelse: React.FC<React.PropsWithChildren<BesvarelseProps>> = ({
  besvarelse,
}) => {
  const { besvarelseId, bestillingId, praksisstedId, kapasitet } = besvarelse;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalBesvarelseId: formatMessage({
        id: translations.modalBesvarelseId.id,
      }),
      modalBestillingId: formatMessage({
        id: translations.modalBestillingId.id,
      }),
      modalPraksisstedId: formatMessage({
        id: translations.modalPraksisstedId.id,
      }),
      modalBesvarelseKapasitet: formatMessage({
        id: translations.modalBesvarelseKapasitet.id,
      }),
      none: formatMessage({
        id: translations.none.id,
      }),
    }),
    [formatMessage],
  );

  const kapasitetMsg = isNullOrUndefined(kapasitet) ? labels.none : kapasitet;

  return (
    <Entry>
      <BoldRegularPair
        boldString={labels.modalBesvarelseId}
        regularString={besvarelseId}
      />
      <BoldRegularPair
        boldString={labels.modalBestillingId}
        regularString={bestillingId}
      />
      <BoldRegularPair
        boldString={labels.modalPraksisstedId}
        regularString={praksisstedId}
      />
      <BoldRegularPair
        boldString={labels.modalBesvarelseKapasitet}
        regularString={`${kapasitetMsg}`}
      />
    </Entry>
  );
};

const mapToBesvarelser = (
  besvarelser: MinimalPraksisbesvarelseDto[],
): JSX.Element[] =>
  besvarelser.map((besvarelse) => (
    <Besvarelse
      key={`besvarelse-${besvarelse.besvarelseId}`}
      besvarelse={besvarelse}
    />
  ));

const Besvarelser: React.FC<React.PropsWithChildren<BesvarelserProps>> = ({
  besvarelserOnSelf,
  besvarelserOnChildren,
}) => {
  const {
    onSelf: showBesvarelserOnSelf,
    onChildren: showBesvarelserOnChildren,
  } = useShowOnSelfOrChildren(besvarelserOnSelf, besvarelserOnChildren);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalBesvarelser: formatMessage({
        id: translations.modalBesvarelser.id,
      }),
      modalBesvarelserVedPraksissted: formatMessage({
        id: translations.modalBesvarelserVedPraksissted.id,
      }),
      modalBesvarelserVedUndernoder: formatMessage({
        id: translations.modalBesvarelserVedUndernoder.id,
      }),
      none: formatMessage({
        id: translations.none.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <PanelHeader title={labels.modalBesvarelser} collapsible>
      {showBesvarelserOnSelf && (
        <SubSection header={labels.modalBesvarelserVedPraksissted}>
          {mapToBesvarelser(besvarelserOnSelf)}
        </SubSection>
      )}
      {showBesvarelserOnChildren && (
        <SubSection header={labels.modalBesvarelserVedUndernoder}>
          {mapToBesvarelser(besvarelserOnChildren)}
        </SubSection>
      )}
      {!showBesvarelserOnSelf && !showBesvarelserOnChildren && labels.none}
    </PanelHeader>
  );
};

const StudentPraksisplass: React.FC<
  React.PropsWithChildren<StudentPraksisplassProps>
> = ({ studentPraksisplass }) => {
  const {
    studentPraksisplassId,
    praksisbesvarelseId,
    studentPraksisklasseId,
    aktivitetId,
  } = studentPraksisplass;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalStudentPraksisplassId: formatMessage({
        id: translations.modalStudentPraksisplassId.id,
      }),
      modalStudentPraksisplassStudentPraksisklasseId: formatMessage({
        id: translations.modalStudentPraksisplassStudentPraksisklasseId.id,
      }),
      modalBesvarelseId: formatMessage({
        id: translations.modalBesvarelseId.id,
      }),
      modalAktivitetId: formatMessage({ id: translations.modalAktivitetId.id }),
    }),
    [formatMessage],
  );

  return (
    <Entry>
      <BoldRegularPair
        boldString={labels.modalStudentPraksisplassId}
        regularString={studentPraksisplassId}
      />
      <BoldRegularPair
        boldString={labels.modalBesvarelseId}
        regularString={praksisbesvarelseId}
      />
      <BoldRegularPair
        boldString={labels.modalStudentPraksisplassStudentPraksisklasseId}
        regularString={studentPraksisklasseId}
      />
      <BoldRegularPair
        boldString={labels.modalAktivitetId}
        regularString={aktivitetId}
      />
    </Entry>
  );
};

const mapToStudentPraksisplasser = (
  studentPraksisplasser: MinimalStudentPraksisplassDto[],
) =>
  studentPraksisplasser.map((studentPraksisplass) => (
    <StudentPraksisplass
      key={`sp-${studentPraksisplass.studentPraksisplassId}`}
      studentPraksisplass={studentPraksisplass}
    />
  ));

const StudentPraksisplasser: React.FC<
  React.PropsWithChildren<StudentPraksisplasserProps>
> = ({ studentPraksisplasserOnSelf, studentPraksisplasserOnChildren }) => {
  const {
    onSelf: showStudentPraksisplasserOnSelf,
    onChildren: showPraksisplasserOnChildren,
  } = useShowOnSelfOrChildren(
    studentPraksisplasserOnSelf,
    studentPraksisplasserOnChildren,
  );

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      studentPraksisplassTitle: formatMessage({
        id: translations.studentPraksisplassTitle.id,
      }),
      modalStudentPraksisplassVedPraksissted: formatMessage({
        id: translations.modalStudentPraksisplassVedPraksissted.id,
      }),
      modalStudentPraksisplassVedUndernoder: formatMessage({
        id: translations.modalStudentPraksisplassVedUndernoder.id,
      }),
      none: formatMessage({
        id: translations.none.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <PanelHeader title={labels.studentPraksisplassTitle} collapsible>
      {showStudentPraksisplasserOnSelf && (
        <SubSection header={labels.modalStudentPraksisplassVedPraksissted}>
          {mapToStudentPraksisplasser(studentPraksisplasserOnSelf)}
        </SubSection>
      )}
      {showPraksisplasserOnChildren && (
        <SubSection header={labels.modalStudentPraksisplassVedUndernoder}>
          {mapToStudentPraksisplasser(studentPraksisplasserOnChildren)}
        </SubSection>
      )}
      {!showStudentPraksisplasserOnSelf &&
        !showPraksisplasserOnChildren &&
        labels.none}
    </PanelHeader>
  );
};

const PraksisstedTilgang: React.FC<
  React.PropsWithChildren<PraksisstedTilgangProps>
> = ({ praksisstedTilgang }) => {
  const { praksisstedId, brukerId, tilgangstype } = praksisstedTilgang;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalPraksisstedTilgangAdmin: formatMessage({
        id: translations.modalPraksisstedTilgangAdmin.id,
      }),
      modalPraksisstedTilgangWrite: formatMessage({
        id: translations.modalPraksisstedTilgangWrite.id,
      }),
      modalPraksisstedTilgangRead: formatMessage({
        id: translations.modalPraksisstedTilgangRead.id,
      }),
      modalPraksisstedTilgangBrukerId: formatMessage({
        id: translations.modalPraksisstedTilgangBrukerId.id,
      }),
      modalPraksisstedTilgangType: formatMessage({
        id: translations.modalPraksisstedTilgangType.id,
      }),
      modalPraksisstedId: formatMessage({
        id: translations.modalPraksisstedId.id,
      }),
    }),
    [formatMessage],
  );

  const tilgangstypeMsg = useMemo(() => {
    switch (tilgangstype) {
      case MinimalPraksisstedtilgangDtoTilgangstypeDtoEnum.Admin:
        return labels.modalPraksisstedTilgangAdmin;
      case MinimalPraksisstedtilgangDtoTilgangstypeDtoEnum.Skriv:
        return labels.modalPraksisstedTilgangWrite;
      case MinimalPraksisstedtilgangDtoTilgangstypeDtoEnum.Les:
        return labels.modalPraksisstedTilgangRead;
    }
  }, [tilgangstype, labels]);

  return (
    <Entry>
      <BoldRegularPair
        boldString={labels.modalPraksisstedId}
        regularString={praksisstedId}
      />
      <BoldRegularPair
        boldString={labels.modalPraksisstedTilgangBrukerId}
        regularString={brukerId}
      />
      <BoldRegularPair
        boldString={labels.modalPraksisstedTilgangType}
        regularString={tilgangstypeMsg}
      />
    </Entry>
  );
};

const mapToPraksisstedTilganger = (
  praksisstedTilganger: MinimalPraksisstedtilgangDto[],
) =>
  praksisstedTilganger.map((praksisstedTilgang) => (
    <PraksisstedTilgang
      key={`sp-${praksisstedTilgang.praksisstedId}-${praksisstedTilgang.brukerId}`}
      praksisstedTilgang={praksisstedTilgang}
    />
  ));

const PraksisstedTilganger: React.FC<
  React.PropsWithChildren<PraksisstedTilgangerProps>
> = ({ praksisstedtilgangerOnSelf, praksisstedtilgangerOnChildren }) => {
  const {
    onSelf: showPraksisstedTilgangerOnSelf,
    onChildren: showPraksisstedTilgangerOnChildren,
  } = useShowOnSelfOrChildren(
    praksisstedtilgangerOnSelf,
    praksisstedtilgangerOnChildren,
  );

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalPraksisstedTilgangTitle: formatMessage({
        id: translations.modalPraksisstedTilgangTitle.id,
      }),
      modalPraksisstedTilgangVedPraksissted: formatMessage({
        id: translations.modalPraksisstedTilgangVedPraksissted.id,
      }),
      modalPraksisstedTilgangVedUndernoder: formatMessage({
        id: translations.modalPraksisstedTilgangVedUndernoder.id,
      }),
      none: formatMessage({
        id: translations.none.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <PanelHeader title={labels.modalPraksisstedTilgangTitle} collapsible>
      {showPraksisstedTilgangerOnSelf && (
        <SubSection header={labels.modalPraksisstedTilgangVedPraksissted}>
          {mapToPraksisstedTilganger(praksisstedtilgangerOnSelf)}
        </SubSection>
      )}
      {showPraksisstedTilgangerOnChildren && (
        <SubSection header={labels.modalPraksisstedTilgangVedUndernoder}>
          {mapToPraksisstedTilganger(praksisstedtilgangerOnChildren)}
        </SubSection>
      )}
      {!showPraksisstedTilgangerOnSelf &&
        !showPraksisstedTilgangerOnChildren &&
        labels.none}
    </PanelHeader>
  );
};

export default PraksisstedInfoModal;
